import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { settingValue, useSettingsStore } from '@/stores/settings.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore, type BilingualLabel } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { halalasToRiyals, riyalsToHalalas, thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { SETTING_SECTIONS, type FieldSchema } from '@/lib/settings-schema';
import {
  BilingualField,
  ChoicesField,
  CodeField,
  LinesField,
  QuantityGuideField,
  SelectField,
  TiersField,
  TextField,
  ToggleField,
  WeekdaysField,
  readBilingual,
  readChoices,
  readQuantityGuide,
  readString,
  readTiers,
  writeBilingual,
  writeChoices,
  type BilingualValue,
} from '@/components/fields';
import { CogIcon } from '@/components/icons';
import { ProductPicker } from '@/components/product-picker';
import { AnnotatedSection, SaveBar } from '@/components/page';
import { Alert, Card, EmptyState, Skeleton, cx } from '@/components/ui';

/**
 * Settings as forms, shared by every settings page.
 *
 * Everything an operator can change is a real field — a labelled input, a
 * switch, a bilingual pair — described once in `lib/settings-schema.ts`.
 * Nobody running a store should have to edit JSON to change a headline, and a
 * stray comma should not be able to break the home page.
 */

/** The settings, loaded once per visit to a settings page. */
export function useSettings() {
  const store = useSettingsStore();
  const { load } = store;

  useEffect(() => {
    void load();
  }, [load]);

  return store;
}

/**
 * The owner's pages, said plainly to anyone else. The API refuses them
 * regardless; this is the courtesy of not showing a form that cannot save.
 */
export function OwnerOnly({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const t = useT();

  if (user?.role !== 'OWNER') {
    return (
      <Card className="flex flex-1 flex-col">
        <EmptyState title={t('ownerOnlySettings')} icon={<CogIcon />} />
      </Card>
    );
  }
  return <>{children}</>;
}

/**
 * Several described settings on one page — each group its own annotated
 * section, all saved from one bar.
 *
 * One save for the page, because an operator setting up "Orders and checkout"
 * changes the numbering and the return window in one sitting and should not
 * have to find two buttons. The groups are still saved one key at a time, in
 * order, and the first refusal stops the rest with its reason on the bar.
 */
export function SchemaSections({ keys }: { keys: readonly string[] }) {
  const { entries, loading, saving, error, save } = useSettings();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const label = (value: BilingualLabel) => value[locale];

  const sections = useMemo(
    () => keys.map((key) => SETTING_SECTIONS.find((section) => section.key === key)).filter((section) => section !== undefined),
    [keys],
  );

  /** What each group's form holds now, by key — only groups someone has touched. */
  const [drafts, setDrafts] = useState<Record<string, Record<string, unknown>>>({});
  /** Bumped on discard, so every form remounts from what is stored. */
  const [version, setVersion] = useState(0);
  const [justSaved, setJustSaved] = useState(false);

  const stored = (key: string) => (settingValue(entries, key) ?? {}) as Record<string, unknown>;
  const dirtyKeys = Object.keys(drafts).filter(
    (key) => JSON.stringify({ ...stored(key), ...drafts[key] }) !== JSON.stringify(stored(key)),
  );

  if (loading && entries.length === 0) {
    return (
      <div className="flex flex-col gap-6">
        {Array.from({ length: 2 }, (_, index) => (
          <Skeleton key={index} className="h-48" />
        ))}
      </div>
    );
  }

  async function saveAll() {
    setJustSaved(false);
    for (const key of dirtyKeys) {
      // Merged, not replaced: a form knows a few keys and the setting holds
      // more (supported locales, ZATCA status, social links).
      const ok = await save(key, { ...stored(key), ...drafts[key] });
      if (!ok) return;
      setDrafts((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
    }
    setJustSaved(true);
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {sections.map((section) => (
          <AnnotatedSection
            key={`${section.key}-${version}`}
            title={label(section.title)}
            description={section.subtitle ? label(section.subtitle) : undefined}
          >
            <Card className="p-5">
              <FieldGrid
                fields={section.fields}
                value={{ ...stored(section.key), ...drafts[section.key] }}
                onChange={(next) => {
                  setJustSaved(false);
                  setDrafts((current) => ({ ...current, [section.key]: next }));
                }}
              />
            </Card>
          </AnnotatedSection>
        ))}
      </div>

      <SaveBar
        saving={saving !== null}
        dirty={dirtyKeys.length > 0}
        message={
          error ? (
            <Alert>{error}</Alert>
          ) : justSaved && dirtyKeys.length === 0 ? (
            <span className="text-success">✓ {t('saved')}</span>
          ) : undefined
        }
        onSave={() => void saveAll()}
        onDiscard={
          dirtyKeys.length > 0
            ? () => {
                setDrafts({});
                setVersion((current) => current + 1);
              }
            : undefined
        }
      />
    </>
  );
}

/** Lays out a schema's fields, giving paragraphs and pairs the full width. */
export function FieldGrid({
  fields,
  value,
  onChange,
}: {
  fields: FieldSchema[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const label = (entry: BilingualLabel) => entry[locale];

  const set = (name: string, next: unknown) => onChange({ ...value, [name]: next });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {fields.map((field) => {
        const wide =
          field.kind === 'bilingual' ||
          field.kind === 'bilingualText' ||
          field.kind === 'weekdays' ||
          field.kind === 'products' ||
          field.kind === 'choices' ||
          field.kind === 'quantityGuide' ||
          field.kind === 'tiers';

        return (
          <div key={field.name} className={cx(wide && 'sm:col-span-2')}>
            {field.kind === 'bilingual' || field.kind === 'bilingualText' ? (
              <BilingualField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                placeholder={field.placeholder ? label(field.placeholder) : undefined}
                multiline={field.kind === 'bilingualText'}
                rows={5}
                value={readBilingual(value[field.name])}
                onChange={(next: BilingualValue) => set(field.name, writeBilingual(next))}
              />
            ) : field.kind === 'weekdays' ? (
              <WeekdaysField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                locale={locale}
                value={Array.isArray(value[field.name]) ? (value[field.name] as number[]) : []}
                onChange={(next) => set(field.name, next)}
              />
            ) : field.kind === 'quantityGuide' ? (
              <QuantityGuideField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={readQuantityGuide(value[field.name])}
                onChange={(next) => set(field.name, next)}
                toThousandths={unitsToThousandths}
                toUnits={thousandthsToUnits}
                labels={{
                  unit: t('guideUnit'),
                  units: { m: t('unit_m'), kg: t('unit_kg'), l: t('unit_l') },
                  arabic: t('choiceArabic'),
                  english: t('choiceEnglish'),
                  amount: t('guideAmount'),
                  add: t('guideAddRow'),
                  remove: t('remove'),
                  noteArabic: t('guideNoteArabic'),
                  noteEnglish: t('guideNoteEnglish'),
                }}
              />
            ) : field.kind === 'tiers' ? (
              <TiersField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={readTiers(value[field.name])}
                onChange={(next) => set(field.name, next)}
                labels={{
                  arabic: t('choiceArabic'),
                  english: t('choiceEnglish'),
                  spend: t('tierSpend'),
                  rate: t('tierRate'),
                  add: t('tierAdd'),
                  remove: t('remove'),
                }}
              />
            ) : field.kind === 'choices' ? (
              <ChoicesField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={readChoices(value[field.name])}
                onChange={(next) => set(field.name, writeChoices(next))}
                labels={{
                  code: t('choiceCode'),
                  arabic: t('choiceArabic'),
                  english: t('choiceEnglish'),
                  add: t('addChoice'),
                  remove: t('remove'),
                }}
              />
            ) : field.kind === 'products' ? (
              <ProductPicker
                ordered
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                selectedIds={Array.isArray(value[field.name]) ? (value[field.name] as string[]) : []}
                onChange={(next) => set(field.name, next.slice(0, 48))}
              />
            ) : field.kind === 'code' ? (
              <CodeField
                id={`setting-${field.name}`}
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={readString(value[field.name])}
                onChange={(next) => set(field.name, next)}
              />
            ) : field.kind === 'lines' ? (
              <LinesField
                id={`setting-${field.name}`}
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={Array.isArray(value[field.name]) ? (value[field.name] as string[]) : []}
                onChange={(next) => set(field.name, next)}
              />
            ) : field.kind === 'toggle' ? (
              <ToggleField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                checked={value[field.name] === true}
                onChange={(next) => set(field.name, next)}
              />
            ) : field.kind === 'select' ? (
              <SelectField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                value={readString(value[field.name])}
                options={(field.options ?? []).map((option) => ({
                  value: option.value,
                  label: label(option.label),
                }))}
                onChange={(next) => set(field.name, next)}
              />
            ) : field.kind === 'money' ? (
              <TextField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                latin
                inputMode="decimal"
                value={typeof value[field.name] === 'number' ? halalasToRiyals(value[field.name] as number) : ''}
                onChange={(next) =>
                  // Empty means "no threshold", which the API models as null.
                  set(field.name, next.trim() === '' ? null : riyalsToHalalas(next))
                }
              />
            ) : field.kind === 'number' ? (
              <TextField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                latin
                inputMode="numeric"
                value={readString(value[field.name])}
                onChange={(next) => set(field.name, next.trim() === '' ? null : Number(next))}
              />
            ) : (
              <TextField
                label={label(field.label)}
                hint={field.hint ? label(field.hint) : undefined}
                placeholder={field.placeholder ? label(field.placeholder) : undefined}
                latin={
                  field.name.toLowerCase().includes('email') ||
                  field.name.toLowerCase().includes('url') ||
                  field.name === 'phone' ||
                  field.name === 'ctaHref' ||
                  field.name === 'href' ||
                  field.name === 'categorySlug' ||
                  field.name === 'defaultCurrency' ||
                  field.name === 'timeZone'
                }
                value={readString(value[field.name])}
                onChange={(next) => set(field.name, next || undefined)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
